import React, { ReactNode } from 'react'
import clsx from 'clsx'

export type DataTableColumnWidth =
    | string
    | undefined
    | {
          width?: string
          className?: string
      }

interface DataTableProps {
    children: ReactNode
    className?: string
    /** Fixed widths for <col> elements — undefined = share remaining space */
    columnWidths?: DataTableColumnWidth[]
}

interface DataTableRowProps {
    children: ReactNode
    className?: string
}

interface DataTableCellProps {
    children: ReactNode
    align?: 'left' | 'right'
    truncate?: boolean
    className?: string
    hiddenOnMobile?: boolean
    /** Override auto first/last rounding when a column is hidden on mobile */
    round?: 'left' | 'right' | 'none'
}

interface DataTableHeaderCellProps {
    children: ReactNode
    align?: 'left' | 'right'
    className?: string
    hiddenOnMobile?: boolean
}

export const DataTable: React.FC<DataTableProps> = ({
    children,
    className,
    columnWidths,
}) => {
    return (
        <div className="mt-6 relative overflow-hidden -mx-4 px-1">
            <table
                className={clsx(
                    'w-full max-w-full border-separate border-spacing-0',
                    className,
                )}
                style={{ tableLayout: 'fixed' }}
            >
                {columnWidths && (
                    <colgroup>
                        {columnWidths.map((column, index) => {
                            const width = typeof column === 'string' ? column : column?.width
                            const colClassName = typeof column === 'object' ? column?.className : undefined

                            return (
                                <col
                                    key={index}
                                    className={colClassName}
                                    style={width ? { width } : undefined}
                                />
                            )
                        })}
                    </colgroup>
                )}
                {children}
            </table>
        </div>
    )
}

export const DataTableHead: React.FC<{ children: ReactNode }> = ({ children }) => (
    <thead>
        <tr className="text-left text-gray-500">{children}</tr>
    </thead>
)

export const DataTableHeaderCell: React.FC<DataTableHeaderCellProps> = ({
    children,
    align = 'left',
    className,
    hiddenOnMobile = false,
}) => {
    return (
        <th
            className={clsx(
                'pb-2 px-2 font-normal first:pl-3 last:pr-3',
                align === 'right' ? 'text-right' : 'text-left',
                hiddenOnMobile && 'hidden md:table-cell',
                className,
            )}
        >
            {children}
        </th>
    )
}

export const DataTableBody: React.FC<{ children: ReactNode }> = ({ children }) => (
    <tbody>{children}</tbody>
)

export const DataTableRow: React.FC<DataTableRowProps> = ({ children, className }) => (
    <tr className={clsx('group cursor-pointer', className)}>
        {children}
    </tr>
)

export const DataTableCell: React.FC<DataTableCellProps> = ({
    children,
    align = 'left',
    truncate = false,
    className,
    hiddenOnMobile = false,
    round,
}) => {
    return (
        <td
            className={clsx(
                'py-1 px-2 group-hover:bg-white/10 transition-colors',
                // Edge padding + radius: use round prop when last col is hidden on mobile
                round === 'left' && 'pl-3 rounded-l-xl',
                round === 'right' && 'pr-3 rounded-r-xl',
                round === undefined && 'first:pl-3 last:pr-3 first:rounded-l-xl last:rounded-r-xl',
                align === 'right' && 'text-right whitespace-nowrap',
                truncate ? 'max-w-0 overflow-hidden' : 'overflow-hidden',
                hiddenOnMobile && 'hidden md:table-cell',
                className,
            )}
        >
            {children}
        </td>
    )
}

export default DataTable
